<template>
  <Dialog :open="show" @update:open="onDialogUpdate">
    <DialogScrollContent @after-leave="onAfterLeave" @interactOutside.prevent>
      <DialogHeader>
        <DialogTitle>
          {{ $t('Add member') }}
        </DialogTitle>
      </DialogHeader>

      <div v-if="!showInviteForm">
        <Alert variant="danger" v-if="error">
          {{ error }}
        </Alert>
        <UserSearch
          ref="userSearchRef"
          :search-url="userResourceUrl"
          :label="$t('Email Address')"
          input-id="add-user-search"
          :autofocus="true"
          @select="addUser"
        >
          <template #action="{ user }">
            <button type="button" class="btn btn-sm btn-outline-primary float-right" @click="addUser(user)">
              {{ $t('Add') }}
            </button>
          </template>
          <template #empty="{ searchQuery }">
            <Empty>
              <template #title>{{ $t('User not found') }}</template>
              <template #description>{{ $t('No user account found for this email. Invite this person to add them as a team member.') }}</template>
              <template #cta>
                <button class="btn btn-primary" @click="onShowInviteForm">
                  {{ $t('Invite this person') }}
                </button>
              </template>
            </Empty>
          </template>
        </UserSearch>
      </div>

      <form v-on:submit.prevent="inviteUser()" v-if="showInviteForm" autocomplete="off">
        <p class="tw:text-sm tw:text-zinc-600">
          {{ $t('Only the email address is required. They will confirm their details and choose their own password from the invitation email.') }}
        </p>

        <div class="form-group">
          <label for="invite-user-firstname">{{ $t('First name') }} <span class="tw:text-zinc-500">({{ $t('optional') }})</span></label>
          <input type="text" id="invite-user-firstname" :class="{ 'is-invalid': inviteErrors.firstname}"
                 class="form-control" v-model="invitee.firstname"
                 autocomplete="off" spellcheck="true" />
          <div class="invalid-feedback" :class="{'d-block' : inviteErrors.firstname}"  v-if="inviteErrors.firstname" v-cloak>
            {{ inviteErrors.firstname[0] }}
          </div>
        </div>
        <div class="form-group">
          <label for="invite-user-lastname">{{ $t('Last name') }} <span class="tw:text-zinc-500">({{ $t('optional') }})</span></label>
          <input type="text" id="invite-user-lastname" :class="{ 'is-invalid': inviteErrors.lastname}"
                 class="form-control" v-model="invitee.lastname"
                 autocomplete="off" spellcheck="true" />
          <div class="invalid-feedback" :class="{'d-block' : inviteErrors.lastname}"  v-if="inviteErrors.lastname" v-cloak>
            {{ inviteErrors.lastname[0] }}
          </div>
        </div>
        <div class="form-group">
          <label for="invite-user-company">{{ $t('Company') }} <span class="tw:text-zinc-500">({{ $t('optional') }})</span></label>
          <input type="text" id="invite-user-company" :class="{ 'is-invalid': inviteErrors.company}"
                 class="form-control" v-model="invitee.company"
                 autocomplete="off" spellcheck="true" />
          <div class="invalid-feedback" :class="{'d-block' : inviteErrors.company}"  v-if="inviteErrors.company" v-cloak>
            {{ inviteErrors.company[0] }}
          </div>
        </div>

        <div class="form-group">
          <label for="invite-user-lang">{{ $t('Language') }}</label>
          <select v-model="invitee.lang" name="lang" id="invite-user-lang"
                  class="form-control" :class="{ 'is-invalid': inviteErrors.lang}">
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
          <div class="invalid-feedback" :class="{'d-block' : inviteErrors.lang}"  v-if="inviteErrors.lang" v-cloak>
            {{ inviteErrors.lang[0] }}
          </div>
        </div>

        <div class="form-group">
          <label for="invite-user-email">{{ $t('Email Address') }}</label>
          <input type="email" id="invite-user-email" :class="{ 'is-invalid': inviteErrors.email}"
                 required class="form-control" v-model="invitee.email"
                 autocapitalize="off" autocomplete="off" spellcheck="true"/>
          <div class="invalid-feedback" :class="{'d-block' : inviteErrors.email}"  v-if="inviteErrors.email" v-cloak>
            {{ inviteErrors.email[0] }}
          </div>
        </div>

        <div class="tw:mt-6 tw:flex tw:flex-col tw:sm:flex-row-reverse tw:gap-3">
          <button type="submit" class="btn btn-primary tw:whitespace-normal" :disabled="isInviting">
            {{ $t('Send the invitation') }}
          </button>
          <button type="button" class="btn btn-light float-right" @click="showInviteForm = false; emits('close')">
            {{ $t('Cancel') }}
          </button>
        </div>
      </form>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { ref } from "vue";
import axios from "axios";
import addrs from "email-addresses";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Alert from "@/components/ui/Alert.vue";
import UserSearch from "@/components/user/UserSearch.vue";
import Empty from "@/components/ui/Empty.vue";

const props = defineProps({
  error: {
    default: null,
  },
  userResourceUrl: {
    required: true,
  },
  userInvitationUrl: {
    required: true,
  },
  permissibleType: {
    required: true,
  },
  permissibleId: {
    required: true,
  },
  show : {
    default : false,
  },
  defaultLocale: {
    type: String,
    default: 'en',
  },
  companyName :{
    type: String,
    default : null,
  }
})

const showInviteForm = ref(false);
const userSearchRef = ref(null);
const isInviting = ref(false);

const invitee = ref({})
const inviteErrors = ref({})

const onShowInviteForm = () => {
  const searchQuery = userSearchRef.value?.searchQuery ?? '';
  const parsed = addrs.parseOneAddress(searchQuery);

  invitee.value = {
    firstname: parsed != null && parsed.name != null ? parsed.name.split(' ')[0] : '',
    lastname: parsed != null && parsed.name != null ? parsed.name.split(' ')[1] : '',
    company: props.companyName,
    email: parsed != null ? parsed.address : '',
    lang: props.defaultLocale,
  };

  userSearchRef.value?.reset();
  showInviteForm.value = true;
}

/**
 * The account and its permissions are created by the same request, so the response is the
 * new permission rather than a user.
 */
const inviteUser = async () => {
  inviteErrors.value = {};
  isInviting.value = true;

  try {
    const { data } = await axios.post(props.userInvitationUrl, {
      ...invitee.value,
      permissible_type: props.permissibleType,
      permissible_id: props.permissibleId,
    });

    emits('memberInvited', data.data);
  } catch (error) {
    if (error.response) {
      inviteErrors.value = error.response.data.message;
    }
  } finally {
    isInviting.value = false;
  }
}

const onAfterLeave = () => {
  userSearchRef.value?.reset();
  showInviteForm.value = false;
}

const emits = defineEmits([
  'close',
  'userSelected',
  'memberInvited',
]);

const addUser = function(user) {
  emits('userSelected', user);
}

const onDialogUpdate = (isOpen) => {
  if (isOpen) {
    userSearchRef.value?.focus();
    showInviteForm.value = false;
  } else {
    emits('close');
  }
}
</script>
